import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/auth.hook';
import { useConfig } from '../config/config.hook';
import { useModeloApresentacao } from '../config/internal/modelo-apresentacao/modelo-apresentacao.hook';
import { useRedirect } from './internal/redirect.hook';
import { authService } from './services/auth.service';
import { Auth, IUsuarioContext, PerfilAluno, PerfilEducador, Props, Provider } from './usuario.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UsuarioContext = createContext<IUsuarioContext<any> | null>(null);

export const UsuarioProvider: Provider = ({ onLoading, children }: Props) => {
  const { contexts, getValueOrThrow } = useConfig();
  const { modelo } = useModeloApresentacao();

  const { authenticated, pending, token } = useAuth();

  const pendenteAutenticacao = pending && !authenticated;
  const erroAutenticacao = !pending && !authenticated;

  const [status, setStatus] = useState<Auth<PerfilAluno | PerfilEducador>>({
    pending: true,
    authorized: false,
    authenticated: false,
    loginProblem: null,
    user: null,
  });

  const { scheduleRedirect } = useRedirect();
  const pendenteAutorizacao = status.pending;

  const salvaStatusAutorizado = useCallback(
    (novoStatus: Auth<PerfilAluno | PerfilEducador>) => {
      setStatus({
        ...novoStatus,
        user: {
          ...novoStatus.user,
          logoff: (motivo) => {
            setStatus({
              pending: false,
              authorized: false,
              authenticated: authenticated,
              loginProblem: motivo,
              user: null,
            });
          },
        },
      });
    },
    [setStatus, authenticated],
  );

  const salvaStatusErro = useCallback(() => {
    setStatus({
      pending: false,
      authorized: false,
      authenticated,
      user: null,
      loginProblem: 'SERVICO_INDISPONIVEL',
    });
  }, [setStatus, authenticated]);

  useEffect(() => {
    if (!pending && authenticated && token) {
      authService<PerfilAluno | PerfilEducador>({
        token,
        url: `${getValueOrThrow<string>('API_DEDICADA')}/me`,
      })
        .then(async (novoStatus: Auth<PerfilAluno | PerfilEducador>) => {
          if (novoStatus.authorized) {
            salvaStatusAutorizado(novoStatus);
          } else {
            setStatus(novoStatus);
          }
        })
        .catch((error) => {
          salvaStatusErro();
          // eslint-disable-next-line no-console
          console.error('Erro ao tentar efetuar a autorização', error);
        });
    }
  }, [salvaStatusAutorizado, salvaStatusErro, authenticated, token, getValueOrThrow, pending]);

  useEffect(() => {
    console.log('Checks de redirecionamento: ', {
      pending: status.pending,
      redirect: contexts.current.redirect,
      modelo,
      autorizado: status.authorized,
      emailUtilizado: status.user?.aura?.dados?.emailUtilizado,
    });
    if (contexts.current.redirect && modelo === 'BROWSER') {
      if (!status.pending && status.authorized) {
        console.info('Evento de redirect logado');
        scheduleRedirect({
          url: `${contexts.current.redirect}?login_hint=${status.user?.aura?.dados?.emailUtilizado}&force_login=true`,
          seconds: 3,
        });
      } else {
        console.info('Evento de redirect DESlogado');
        scheduleRedirect({
          url: `${contexts.current.redirect}`,
          seconds: 3,
        });
      }
    }
  }, [
    modelo,
    status.pending,
    contexts.current.redirect,
    status.authorized,
    scheduleRedirect,
    status.user?.aura?.dados?.emailUtilizado,
    contexts,
  ]);

  const result = useMemo(
    () => ({
      autorizado: status.authorized,
      autenticado: authenticated,
      pendente: pending,
      usuario: status.user,
      problemaLogin: status.loginProblem,
    }),
    [status.authorized, authenticated, pending, status.user, status.loginProblem],
  );

  if ((pendenteAutenticacao || pendenteAutorizacao) && !erroAutenticacao) {
    return <>{onLoading}</>;
  }

  return <UsuarioContext.Provider value={result}>{children}</UsuarioContext.Provider>;
};
